import type { HermesAPI } from "./api";
import { HermesError } from "./errors";
import { arr, bool, int, obj, params, s, str, type JsonObject } from "./json";

// MARK: 技能（REST /api/skills：带来源、启用状态、使用次数）

export interface SkillInfo {
  name: string;
  description?: string;
  category?: string;
  enabled: boolean;
  usage: number;
  /** hub = 从技能中心安装；bundled = Hermes 自带；agent = 自己写的或 agent 创建的 */
  provenance: "hub" | "bundled" | "agent";
}

/** 用户自己添加的（从技能中心装的，或自己/agent 写的） */
export function skillUserAdded(x: SkillInfo): boolean {
  return x.provenance !== "bundled";
}

export async function skillCatalog(api: HermesAPI, profile?: string): Promise<SkillInfo[]> {
  const raw = await api.client.broker.getJSON("/api/skills", { profile });
  return (arr(raw) ?? []).map(obj).filter(Boolean).flatMap((x) => {
    const name = s(x!.name);
    if (!name) return [];
    const p = s(x!.provenance);
    return [{
      name, description: s(x!.description), category: s(x!.category), enabled: bool(x!.enabled) ?? true,
      usage: int(x!.usage) ?? 0, provenance: p === "hub" || p === "agent" ? p : "bundled",
    } as SkillInfo];
  });
}

/** 启用 / 停用一个技能（停用后不出现在 agent 的技能目录里） */
export async function setSkillEnabled(api: HermesAPI, name: string, enabled: boolean, profile?: string): Promise<void> {
  await api.client.broker.sendJSON("PUT", "/api/skills/toggle", params({ name, enabled, profile }));
}

/** 删除一个自己添加的技能：技能中心装的走官方卸载；自己或 agent 写的走官方“归档”（可恢复）。内置技能只能停用。 */
export async function deleteSkill(api: HermesAPI, skill: SkillInfo, profile?: string): Promise<void> {
  switch (skill.provenance) {
    case "hub":
      await api.client.broker.sendJSON("POST", "/api/skills/hub/uninstall", params({ name: skill.name, profile }));
      return;
    case "agent": {
      const r = obj(await api.client.broker.sendJSON("DELETE", "/api/learning/node", params({ id: skill.name, profile })));
      if (bool(r?.ok) === false) throw HermesError.http(400, str(r?.message) ?? "删除失败");
      return;
    }
    default:
      throw HermesError.invalidConfiguration("内置技能不能删除，可以停用");
  }
}

/** 技能说明书原文（SKILL.md） */
export async function skillContent(api: HermesAPI, name: string, profile?: string): Promise<string> {
  return s(obj(await api.client.broker.getJSON("/api/skills/content", { name, profile }))?.content) ?? "";
}

// MARK: MCP 连接器

export interface MCPCatalogEntry {
  name: string;
  description: string;
  installed: boolean;
  enabled: boolean;
  /** none / oauth / api_key */
  auth_type: string;
  required_env: { name: string; prompt?: string; required?: boolean }[];
  transport: string;
  url?: string;
}

/** 需要本机程序或本地服务（连的是远程主机，这类用不了） */
export function mcpLocalOnly(e: MCPCatalogEntry): boolean {
  return e.transport !== "http" || (e.url ? /127\.0\.0\.1|localhost|\$\{/.test(e.url) : false);
}

export interface MCPServer {
  name: string;
  transport?: string;
  url?: string;
  /** oauth / header */
  auth?: string;
  oauth_tokens_present?: boolean;
  enabled: boolean;
}

/** 需要登录授权但还没授权 */
export function mcpNeedsAuthorization(x: MCPServer): boolean {
  return x.auth === "oauth" && x.oauth_tokens_present !== true;
}

export async function mcpCatalog(api: HermesAPI, profile?: string): Promise<MCPCatalogEntry[]> {
  const raw = obj(await api.client.broker.getJSON("/api/mcp/catalog", { profile }));
  return (arr(raw?.entries) ?? []).map(obj).filter(Boolean).flatMap((e) => {
    const name = s(e!.name);
    if (!name) return [];
    return [{
      name, description: s(e!.description) ?? "", installed: bool(e!.installed) ?? false, enabled: bool(e!.enabled) ?? false,
      auth_type: s(e!.auth_type) ?? "none", transport: s(e!.transport) ?? "http", url: s(e!.url),
      required_env: (arr(e!.required_env) ?? []).map(obj).filter(Boolean).map((v) => ({ name: s(v!.name) ?? "", prompt: s(v!.prompt), required: bool(v!.required) })),
    }];
  });
}

export async function mcpServers(api: HermesAPI, profile?: string): Promise<MCPServer[]> {
  const raw = obj(await api.client.call("mcp.servers.list", params({ profile })));
  return (arr(raw?.servers) ?? []).map(obj).filter(Boolean).flatMap((x) => {
    const name = s(x!.name);
    return name ? [{ name, transport: s(x!.transport), url: s(x!.url), auth: s(x!.auth), oauth_tokens_present: bool(x!.oauth_tokens_present), enabled: bool(x!.enabled) ?? true }] : [];
  });
}

/** 从官方目录安装（api_key 类型的把密钥一起传过去，服务器只写进 .env） */
export async function installMCP(api: HermesAPI, name: string, env: Record<string, string>, profile?: string): Promise<void> {
  const body: JsonObject = { name, enable: true, env };
  if (profile) body.profile = profile;
  await api.client.broker.sendJSON("POST", "/api/mcp/catalog/install", body, { profile });
}

export async function removeMCP(api: HermesAPI, name: string, profile?: string): Promise<void> {
  await api.client.call("mcp.servers.remove", params({ name, profile }));
}

/** 连一下试试：能列出工具就说明可用 */
export async function testMCP(api: HermesAPI, name: string, profile?: string): Promise<{ ok: boolean; error?: string; toolNames: string[]; oauthNeeded: boolean }> {
  const raw = obj(await api.client.call("mcp.servers.test", params({ name, profile }), { timeout: 120 }));
  return {
    ok: bool(raw?.ok) ?? false, error: str(raw?.error),
    toolNames: (arr(raw?.tools) ?? []).map((t) => str(obj(t)?.name)).filter((n): n is string => !!n),
    oauthNeeded: bool(raw?.oauth_needed) ?? false,
  };
}

export interface MCPOAuthFlow { flowID: string; status: string; authorizationURL?: string; error?: string }

function parseFlow(raw: unknown): MCPOAuthFlow {
  const o = obj(raw);
  return { flowID: s(o?.flow_id) ?? "", status: s(o?.status) ?? "starting", authorizationURL: s(o?.authorization_url), error: s(o?.error) };
}

/** 开始 OAuth 授权。回调地址是服务器自己的公开地址，这里只需打开登录页 */
export async function startMCPOAuth(api: HermesAPI, name: string, profile?: string): Promise<MCPOAuthFlow> {
  return parseFlow(await api.client.broker.sendJSON("POST", `/api/mcp/servers/${encodeURIComponent(name)}/auth`, {}, { profile }));
}

export async function mcpOAuthStatus(api: HermesAPI, flowID: string): Promise<MCPOAuthFlow> {
  return parseFlow(await api.client.broker.getJSON(`/api/mcp/oauth/flows/${encodeURIComponent(flowID)}`));
}

export async function cancelMCPOAuth(api: HermesAPI, flowID: string): Promise<void> {
  try { await api.client.broker.sendJSON("DELETE", `/api/mcp/oauth/flows/${encodeURIComponent(flowID)}`, {}); } catch { /* 已经结束 */ }
}

/** 让正在运行的会话重新加载 MCP 工具（新连接的服务马上能用） */
export async function reloadMCP(api: HermesAPI): Promise<void> {
  await api.client.call("reload.mcp", { confirm: true }, { timeout: 120 });
}
